import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Fragment, useMemo } from "react";

/**
 * A deliberately small Markdown renderer for article bodies.
 *
 * The editorial format is fixed and narrow — `##` headings, `**bold**`, `-` bullets,
 * and paragraphs — so a full parser plus a sanitiser would be several hundred
 * kilobytes to support syntax no article uses. Everything here is rendered as React
 * elements from parsed text; no HTML string ever reaches the DOM, so untrusted content
 * cannot inject markup.
 *
 * When the CMS lands and authors need tables, links or images, replace this with a
 * real parser *and* a sanitiser — not one without the other.
 */

/** Split a line into runs, turning `**bold**` into `<strong>`. */
function renderInline(text: string): React.ReactNode {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return (
        <Box
          key={index}
          component="strong"
          sx={{ fontWeight: 600, color: "text.primary" }}
        >
          {part.slice(2, -2)}
        </Box>
      );
    }
    return <Fragment key={index}>{part}</Fragment>;
  });
}

type Block =
  | { kind: "heading"; text: string }
  | { kind: "paragraph"; text: string }
  | { kind: "list"; items: string[] };

function parse(markdown: string): Block[] {
  const blocks: Block[] = [];
  let bullets: string[] = [];

  const flushBullets = () => {
    if (bullets.length > 0) {
      blocks.push({ kind: "list", items: bullets });
      bullets = [];
    }
  };

  for (const rawLine of markdown.split("\n")) {
    const line = rawLine.trim();

    if (line.startsWith("- ")) {
      bullets.push(line.slice(2));
      continue;
    }
    flushBullets();

    if (line.length === 0) continue;
    if (line.startsWith("#")) {
      blocks.push({ kind: "heading", text: line.replace(/^#+\s*/, "") });
    } else {
      blocks.push({ kind: "paragraph", text: line });
    }
  }
  flushBullets();

  return blocks;
}

export function Markdown({ children }: { children: string }) {
  const blocks = useMemo(() => parse(children), [children]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {blocks.map((block, index) => {
        if (block.kind === "heading") {
          return (
            <Typography
              key={index}
              variant="H3"
              sx={{ color: "text.primary", marginTop: index === 0 ? 0 : 2 }}
            >
              {block.text}
            </Typography>
          );
        }

        if (block.kind === "list") {
          return (
            <Box
              key={index}
              component="ul"
              sx={{
                margin: 0,
                paddingLeft: 2.5,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {block.items.map((item, itemIndex) => (
                <Typography
                  key={itemIndex}
                  component="li"
                  variant="T2"
                  sx={{ color: "text.secondary" }}
                >
                  {renderInline(item)}
                </Typography>
              ))}
            </Box>
          );
        }

        return (
          <Typography key={index} variant="T1" sx={{ color: "text.secondary" }}>
            {renderInline(block.text)}
          </Typography>
        );
      })}
    </Box>
  );
}
